import { NotFoundLoaderResponse } from '@/errors';
import {
  AliasHandlingQuery,
  AliasHandlingQueryVariables,
  ArticlePageFragment,
  CompositionPageFragment,
} from '@/gql/graphql';
import { LoaderArgs } from '@/reactRouterPlugins';
import { throwCriticalErrors } from '@/routes/rootErrorPage';
import { useLoaderData } from 'react-router-dom';
import { ArticlePage, articlePageLoader } from './article/article';
import { ArticleSkeleton } from './components/articleSkeleton';
import { CompositionPage, compositionPageLoader } from './composition/composition';
import { ResourceLoaderResult } from './createResourceLoaderWithRedirect';

export const AliasHandlingSkeleton = ArticleSkeleton;

const ALIAS_HANDLING_FRAGMENT = /* GraphQL */ `
  query AliasHandling($alias: String!) {
    resource(alias: $alias) {
      ...ResourceRedirectDetails
    }
  }
`;

export async function aliasHandlingLoader(args: LoaderArgs) {
  const { graphql, params } = args;

  const alias = params['*'];

  // some sanity check to prevent unnecessary GraphQL calls
  if (typeof alias !== 'string' || alias.trim() === '') {
    throw new NotFoundLoaderResponse();
  }
  const startsWithSubstrings = [
    'api/',
    'content/',
    'occurrence/',
    'species/',
    'dataset/',
    'publisher/',
    'collection/',
    'institution/',
    'resource/',
    'composition/',
    'article/',
    'data-use/',
    'document/',
  ];
  const includesSubstrings = ['.', ...startsWithSubstrings.map((substring) => `/${substring}`)];
  // if an alias starts with or includes any of the substrings, we can be pretty sure it is not a
  // valid alias and we can skip the GraphQL call
  const ignoreAlias =
    startsWithSubstrings.some((substring) => alias.startsWith(substring)) ||
    includesSubstrings.some((substring) => alias.includes(substring));
  if (ignoreAlias) {
    throw new NotFoundLoaderResponse();
  }

  const response = await graphql.query<AliasHandlingQuery, AliasHandlingQueryVariables>(
    ALIAS_HANDLING_FRAGMENT,
    { alias: `/${alias}` }
  );

  const { errors, data } = await response.json();
  throwCriticalErrors({
    path404: ['resource'],
    errors,
    requiredObjects: [data?.resource],
  });

  const resource = data.resource!;

  if ('urlAlias' in resource) {
    const loader = (() => {
      switch (resource.__typename) {
        case 'Article':
          return articlePageLoader;
        case 'Composition':
          return compositionPageLoader;
      }
    })();

    if (typeof loader !== 'function') {
      console.error(`No loader found for resource type ${resource.__typename}`);
      throw new NotFoundLoaderResponse();
    }

    return loader({ ...args, params: { key: resource.id } });
  }

  throw new NotFoundLoaderResponse();
}

export function AliasHandling() {
  const { data } = useLoaderData() as ResourceLoaderResult<
    ArticlePageFragment | CompositionPageFragment
  >;
  const { resource } = data;

  switch (resource.__typename) {
    case 'Article':
      return <ArticlePage />;
    case 'Composition':
      return <CompositionPage />;
  }
}
